import config from "react-global-configuration"
import { put, select, takeLatest, takeLeading } from "redux-saga/effects"

import { openNotification } from "components/Message/Message"
import { request } from "lib/request"
import { languages } from "locales/languages"

import actions from "./actions"
import {
  PREVENTION_HANDOFF_CANCEL_REQUEST,
  PREVENTION_HANDOFF_CREATE_REQUEST,
  PREVENTION_HANDOFF_SMS_REQUEST,
  PREVENTION_HANDOFF_STATUS_REQUEST,
} from "./constants"
import { getPreventionHandoffLink } from "./selector"

const handoffUrl = (id?: string, suffix = "") => `${config.get("consultation.prevention.handoff")}${id ? `/${id}` : ""}${suffix}`

// Issued before the patient choice: the patient is chosen on the phone
function* createHandoff(): any {
  const url = handoffUrl()
  try {
    const response = yield request(url, {
      method: "POST",
      payload: {},
    })
    if (response.status !== "ok") throw new Error(response.status)
    yield put(
      actions.createSuccess({
        id: response.handoff_id,
        url: response.link,
        created_at: response.created_at,
        end_at: response.end_at,
        received_at: Date.now(),
        status: "pending",
      }),
    )
  } catch (err) {
    console.error(err, { route: url })
    openNotification({ type: "error", text: languages.preventionHandoffLinkError })
    yield put(actions.failure())
  }
}

function* fetchStatus(): any {
  const link = yield select(getPreventionHandoffLink)
  if (!link) return
  const url = handoffUrl(link.id)
  try {
    const response = yield request(url, { method: "GET" })
    if (response.status === "ok" && response.data?.status) {
      yield put(actions.statusSuccess(response.data.status))
    }
  } catch (err) {
    // Polling: a failed poll is retried on the next tick
    console.error(err, { route: url })
  }
}

function* sendSms(): any {
  const link = yield select(getPreventionHandoffLink)
  if (!link) return
  const url = handoffUrl(link.id, "/sms")
  try {
    const response = yield request(url, { method: "POST" })
    if (response.status !== "ok") throw new Error(response.status)
  } catch (err) {
    console.error(err, { route: url })
    openNotification({ type: "error", text: languages.preventionHandoffLinkError })
  }
}

// Cancellation is fire-and-forget: the store is reset right away so the screen moves on, then the
// API is told to refuse the phone link; expiry covers a lost request. The reducer must not reset
// on the request itself: reducers run before sagas, the id would be gone before it is read.
function* cancelHandoff(): any {
  const link = yield select(getPreventionHandoffLink)
  yield put(actions.reset())
  if (!link) return
  const url = handoffUrl(link.id)
  try {
    yield request(url, { method: "DELETE" })
  } catch (err) {
    console.error(err, { route: url })
  }
}

export default function* rootSaga() {
  yield takeLatest(PREVENTION_HANDOFF_CREATE_REQUEST, createHandoff)
  yield takeLatest(PREVENTION_HANDOFF_STATUS_REQUEST, fetchStatus)
  yield takeLeading(PREVENTION_HANDOFF_SMS_REQUEST, sendSms)
  yield takeLatest(PREVENTION_HANDOFF_CANCEL_REQUEST, cancelHandoff)
}
